import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ───────────────────────────────────────────────────────────────────

export type EmailProvider = 'outlook' | 'google';

export type EmailSyncStatus = 'active' | 'error' | 'disconnected';

export interface EmailSyncAccount {
  id: string;
  tenantId: string;
  userId: string;
  provider: EmailProvider;
  email: string;
  status: EmailSyncStatus;
  lastSyncAt: string | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SyncedEmail {
  id: string;
  tenantId: string;
  accountId: string;
  messageId: string;
  subject: string;
  from: string;
  to: string[];
  cc: string[];
  receivedAt: string;
  snippet: string;
  hasAttachments: boolean;
  linkedContactId: string | null;
  linkedContactName: string | null;
  linkedJobId: string | null;
  linkedJobTitle: string | null;
  isRead: boolean;
  createdAt: string;
}

// ─── Request Types ───────────────────────────────────────────────────────────

export interface EmailsListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  accountId?: string;
  linked?: 'all' | 'linked' | 'unlinked';
}

export interface LinkEmailRequest {
  contactId?: string;
  jobId?: string;
}

/** Paginated response */
interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

// ─── Query Keys ──────────────────────────────────────────────────────────────

const emailSyncKeys = {
  all: ['email-sync'] as const,
  accounts: () => [...emailSyncKeys.all, 'accounts'] as const,
  emails: () => [...emailSyncKeys.all, 'emails'] as const,
  emailList: (params: EmailsListParams) => [...emailSyncKeys.emails(), params] as const,
};

// ─── Hooks ───────────────────────────────────────────────────────────────────

/**
 * Hook to fetch connected email sync accounts.
 * Validates: Requirements 22.1, 22.2
 */
export function useEmailSyncAccounts() {
  return useQuery({
    queryKey: emailSyncKeys.accounts(),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<EmailSyncAccount[]>>('/email-sync/accounts');
      return response.data.data;
    },
  });
}

/**
 * Hook to connect an Outlook account via OAuth.
 */
export function useConnectOutlook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<ApiResponse<EmailSyncAccount>>(
        '/email-sync/connect/outlook',
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailSyncKeys.accounts() });
    },
  });
}

/**
 * Hook to connect a Google account via OAuth.
 */
export function useConnectGoogle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<ApiResponse<EmailSyncAccount>>(
        '/email-sync/connect/google',
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailSyncKeys.accounts() });
    },
  });
}

/**
 * Hook to disconnect an email sync account.
 */
export function useDisconnectAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (accountId: string) => {
      await apiClient.delete<ApiResponse<void>>(`/email-sync/accounts/${accountId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailSyncKeys.accounts() });
      queryClient.invalidateQueries({ queryKey: emailSyncKeys.emails() });
    },
  });
}

/**
 * Hook to fetch synced emails with pagination and filtering.
 * Validates: Requirements 22.1, 22.2
 */
export function useEmailsList(params: EmailsListParams = {}) {
  return useQuery({
    queryKey: emailSyncKeys.emailList(params),
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.set('page', String(params.page));
      if (params.pageSize) queryParams.set('pageSize', String(params.pageSize));
      if (params.search) queryParams.set('search', params.search);
      if (params.accountId) queryParams.set('accountId', params.accountId);
      if (params.linked) queryParams.set('linked', params.linked);

      const response = await apiClient.get<ApiResponse<PaginatedResponse<SyncedEmail>>>(
        `/email-sync/emails?${queryParams.toString()}`,
      );
      return response.data.data;
    },
  });
}

/**
 * Hook to link an email to a contact and/or job.
 * Validates: Requirements 22.3, 22.4
 */
export function useLinkEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ emailId, data }: { emailId: string; data: LinkEmailRequest }) => {
      const response = await apiClient.post<ApiResponse<SyncedEmail>>(
        `/email-sync/emails/${emailId}/link`,
        data,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailSyncKeys.emails() });
    },
  });
}
